import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router";
import { Toaster } from "sonner";
import { App } from "./App";
import { ImpersonationBanner } from "./components/admin/ImpersonationBanner";
import { AcceptInvitationPage } from "./components/auth/AcceptInvitationPage";
import { AcceptPlatformInvitationPage } from "./components/auth/AcceptPlatformInvitationPage";
import { LoginForm } from "./components/auth/LoginForm";
import { DashboardRenderer } from "./components/dashboard/DashboardRenderer";
import { PlatformStatusProvider, usePlatformStatus } from "./lib/PlatformStatusContext";
import { useSession } from "./lib/auth-client";
import { initTheme } from "./lib/console-theme";
import "./styles.css";

// Aplica data-theme en <html> ANTES del primer paint de React (evita flash
// de tema incorrecto). Se ejecuta a nivel de módulo, no dentro de un
// componente, para correr síncronamente antes de createRoot().render().
initTheme();

// Consola de plataforma /admin/* — lazy para que los usuarios de tenant
// que nunca entren a /admin no descarguen el JS de la consola.
const AdminShell = lazy(() =>
  import("./components/admin/AdminShell").then((m) => ({ default: m.AdminShell })),
);

/**
 * Página de un dashboard publicado para el tenant.
 *
 * Guard mínimo: si no hay sesión → login.
 * El gate real de membresía lo hace el worker (GET /api/dashboards/:id → 404 si
 * el actor no es miembro de la org dueña del dashboard).
 */
function TenantDashboardPage() {
  const { data: session, isPending } = useSession();
  const { id } = useParams<{ id: string }>();

  if (isPending) {
    return (
      <div className="loading-overlay show">
        <div className="spinner" />
      </div>
    );
  }

  if (!session) return <LoginForm />;
  if (!id) return <Navigate to="/" replace />;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0d1117",
        color: "var(--color-text)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <DashboardRenderer dashboardId={id} />
    </div>
  );
}

/**
 * Ruta "/" con redirect a /admin para platform members.
 *
 * Los platform members (superAdmin, studio-admin, studio-staff) NO tienen una
 * organización activa de tenant → caerían en el error "No se pudieron cargar
 * las secciones" del BI. Los redirigimos directamente a /admin.
 *
 * Usuarios tenant (isPlatformMember=false) ven el BI normalmente.
 * Mientras se resuelve el estado de plataforma, se muestra el BI (no bloqueamos).
 */
function DashboardOrConsole() {
  const { isPlatformMember, state } = usePlatformStatus();
  const { data: session } = useSession();

  // Sin sesión → App.tsx muestra LoginForm
  if (!session) return <App />;

  // Estado de plataforma resuelto: redirigir a /admin
  if (state.phase === "ready" && isPlatformMember) {
    return <Navigate to="/admin" replace />;
  }

  // Cargando o sin membresía de plataforma → BI dashboard
  return <App />;
}

/**
 * Wrapper raíz que lee la sesión y provee el contexto de estado de plataforma.
 * Se monta dentro de BrowserRouter para que useSession (Better Auth) funcione.
 * PlatformStatusProvider hace UNA sola llamada a /impersonate/status por carga
 * y la comparte con App.tsx (botón Consola) y AdminShell (guard de acceso).
 */
function AppRoot() {
  const { data: session } = useSession();
  const hasSession = !!session;

  return (
    <PlatformStatusProvider hasSession={hasSession}>
      {/*
       * Banner global de impersonación: sticky top, visible en toda la app.
       * Se monta siempre pero solo renderiza contenido cuando impersonating=true.
       * NO depende de useSession — usa PlatformStatusContext (/impersonate/status).
       * Fase 3: GOTCHA — getSession sigue devolviendo al admin durante impersonación.
       */}
      <ImpersonationBanner />
      {/*
       * Toaster global de Sonner: feedback de éxito/error de TODA mutación de
       * plataforma (crear/editar/eliminar rol, asignar/cambiar/revocar membresía).
       * theme="dark" + tokens del tema oscuro legacy (theme.css) vía CSS vars.
       * richColors da verde/rojo coherentes con --color-success / --color-danger.
       * Posición top-right: no choca con el banner sticky ni con los modales.
       */}
      <Toaster
        theme="dark"
        position="top-right"
        richColors
        closeButton
        toastOptions={{
          style: {
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            color: "var(--color-text)",
            fontFamily: "var(--font-sans)",
          },
        }}
      />
      <Routes>
        {/*
         * Ruta pública de aceptación de invitaciones.
         * No requiere sesión — el invitado puede ser un usuario nuevo.
         * Se registra ANTES de "/*" para que el router la matchee con prioridad.
         */}
        <Route path="/accept-invitation/:invitationId" element={<AcceptInvitationPage />} />
        {/*
         * Ruta pública de aceptación de invitaciones de PLATAFORMA (ADR 0012).
         * Router paralelo al de tenant — endpoints /api/auth/platform-invitation/*.
         */}
        <Route
          path="/accept-platform-invitation/:invitationId"
          element={<AcceptPlatformInvitationPage />}
        />
        {/*
         * Dashboard publicado para el tenant.
         * Guard: requiere sesión (server-side gate: membresía en la org).
         * Se registra ANTES de "/*" para prioridad correcta.
         */}
        <Route path="/dashboard/:id" element={<TenantDashboardPage />} />
        {/*
         * "/" — platform members → /admin; usuarios tenant → BI.
         * AdminShell tiene su propio guard: si alguien entra directo a /admin
         * sin sesión, lo manda al login. Si entra sin membership, muestra 403.
         */}
        <Route path="/*" element={<DashboardOrConsole />} />
        {/* Consola de plataforma — chunk lazy, solo baja si el usuario navega */}
        <Route
          path="/admin/*"
          element={
            <Suspense
              fallback={
                <div className="loading-overlay show">
                  <div className="spinner" />
                </div>
              }
            >
              <AdminShell />
            </Suspense>
          }
        />
      </Routes>
    </PlatformStatusProvider>
  );
}

const container = document.getElementById("root");
if (!container) throw new Error("#root not found");

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <AppRoot />
    </BrowserRouter>
  </StrictMode>,
);
